import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES, CONTACT } from "../mock";
import Seo from "../components/Seo";

const CREW = [
  {
    name: "Hunter",
    image: "hzHunter",
    quote:
      "“I love teaching surf lessons because I get to help people push past fear and experience something new. Seeing someone stand up on their first wave and feel that confidence is the best part. It’s rewarding to share something I’m passionate about and help others grow through it. Surfing requires constant learning and adapting to changing conditions, which keeps me mentally engaged and challenged. Being in the ocean keeps me present and has taught me patience, resilience, and respect for both the environment and the process of growth.”",
  },
  {
    name: "Zion",
    image: "hzZion",
    quote:
      "“Hello, my name is Coach Zion. I can’t wait to catch some sick waves and get you shredding in no time. The only things I ask are that you try your best, keep a positive attitude, and most importantly have fun. Surfing is all about enjoying the ocean and pushing yourself to learn something new.”",
  },
  {
    name: "Garrett",
    image: "hzGarrett",
    quote:
      "“I love surfing because it helps me stay present in the moment and clear my mind of stress. The highs and lows of swells resemble the unpredictability of life, which keeps me humble and adaptable both on land and at sea. Surfing keeps me conscious of my health, pushing me to stay flexible, strong, and connected to the ocean. When I’m in the water I feel connected to the universe — waves are like a gift from our creator. Teaching surf lessons is amazing to me because I get to share that joy and see someone stand up for the first time and experience the stoke of surfing for themselves.”",
  },
  {
    name: "Nate",
    image: "hzNate",
    quote:
      "“Surfing has always given me a sense of freedom that’s hard to find anywhere else. Time in the ocean has a way of clearing your head and reminding you what really matters. I help with surf lessons and run the jet ski for step-offs and safety when we’re working the lineup. I enjoy helping people feel comfortable in the water and watching them catch waves they didn’t think they could. For me it’s really about sharing the stoke and making sure everyone leaves the ocean with a smile.”",
  },
  {
    name: "Megan",
    image: "hzMegan",
    quote:
      "“I love surfing because it allows me to connect not only with nature, but also with myself. When I’m on a wave everything else seems to pause, and the ocean always has a way of lifting away any stress or anxiety. Surfing has been almost like therapy for me—it uplifts me spiritually, mentally, and physically. Teaching lessons is meaningful because I get to share that same stoke and watch someone who was once scared of the ocean stand up on their first wave with a huge smile. Helping people discover the beauty of the ocean and the healthy, happy lifestyle that comes with surfing is what makes being a surf instructor so special to me.”",
  },
];

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="bg-stone-50">
      <Seo
        title="About Us | Founder Scott Hamrick & Our Crew"
        description="Hang Zen Surf + Sea was founded in 2005 by lifelong waterman Scott Hamrick — one of the first three permitted surf schools in North County San Diego. Meet our certified instructors and USCG-licensed captains."
        path="/about-us"
      />
      {/* Hero - split layout so portrait photo shows fully */}
      <section className="relative bg-[#0b3a5f] pt-28 pb-12 md:pt-32 md:pb-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div className="md:py-16">
            <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-semibold mb-3">
              Our Story
            </div>
            <h1
              className="text-5xl md:text-7xl text-white drop-shadow mb-5"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              About Us
            </h1>
            <p className="text-white/85 text-lg leading-relaxed max-w-md">
              Founded in 2005 by lifelong waterman Scott Hamrick — the original Encinitas surf school and the soul of Hang Zen Surf + Sea.
            </p>
          </div>
          <div className="relative flex justify-center md:justify-end">
            <div className="relative w-full max-w-md md:max-w-lg aspect-square rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white/10">
              <img
                src="/about-hero.jpg"
                alt="Scott Hamrick — Founder of Hang Zen Surf + Sea"
                className="w-full h-full"
                style={{ objectFit: "cover", objectPosition: "center 25%" }}
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-orange-400/30 blur-3xl -z-0" />
            <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-amber-300/30 blur-3xl -z-0" />
          </div>
        </div>
      </section>

      {/* Founder Scott Hamrick */}
      <section className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={IMAGES.hzScott}
                alt="Founder Scott Hamrick"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 w-32 h-32 rounded-full bg-orange-300/40 blur-3xl -z-10" />
            <div className="absolute -top-5 -left-5 w-32 h-32 rounded-full bg-sky-300/40 blur-3xl -z-10" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-3">
              Founder
            </div>
            <h2
              className="text-4xl md:text-5xl text-slate-900 mb-6"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              Scott Hamrick
            </h2>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Hang Zen Surf + Sea was founded in Encinitas in 2005 by lifelong waterman Scott Hamrick. The school was one of the first three permitted surf schools in North County San Diego, helping set the standard for safe, professional surf instruction along the coast.
              </p>
              <p>
                Scott, a La Jolla native, has decades of experience in Southern California surf and offshore conditions. A former competitive surfer, seasoned fishing captain, and licensed 100-Ton Master Captain, he has spent years navigating local waters and chasing fish up and down the coast.
              </p>
              <p>
                His knowledge of tides, weather patterns, and seasonal movement runs deep. Known for his authentic stoke and ability to read the water, Scott adapts each session to the day’s conditions and the group on board.
              </p>
              <p>
                EMT certified and safety-focused, he brings professionalism, confidence, and respect for the ocean to every charter and lesson. Off the clock, he’s usually still in the water — with family, chasing swell or following the bite.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Instructors banner */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hzInstructors})` }}
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h3
            className="text-4xl md:text-5xl text-white mb-5"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            Our Instructors
          </h3>
          <p className="text-white/95 text-lg leading-relaxed">
            Our instructors and captains are seasoned locals who love what they do. Whether you’re a first-time surfer, a seasoned rider, or a curious angler, Hang Zen has the experience and energy to meet you where you are and take you further.
          </p>
        </div>
      </section>

      {/* Meet the Crew */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-3">
              The Crew
            </div>
            <h2
              className="text-4xl md:text-5xl text-slate-900"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              Meet the Crew
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {CREW.map((c) => (
              <CrewCard key={c.name} crew={c} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-[#0b3a5f] rounded-3xl p-10 md:p-14 text-center text-white shadow-2xl">
            <h2
              className="text-4xl md:text-5xl mb-4"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              Come surf with us.
            </h2>
            <p className="text-white/85 max-w-xl mx-auto mb-7">
              Whether you’re standing on a board for the first time or chasing your first bluefin, we’d love to share the ocean with you. Hit us up — we’ll find the right session.
            </p>
            <div className="flex gap-3 flex-wrap justify-center">
              <Link
                to="/book-today"
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full transition-colors"
              >
                Book Today <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold rounded-full transition-colors"
              >
                Call {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CrewCard = ({ crew }) => {
  const [expanded, setExpanded] = React.useState(false);
  const limit = 220;
  const isLong = crew.quote.length > limit;
  const display = expanded || !isLong ? crew.quote : crew.quote.slice(0, limit).trim() + "…";

  return (
    <div className="text-center">
      <div className="relative mx-auto w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden shadow-xl ring-4 ring-white">
        <img
          src={IMAGES[crew.image]}
          alt={crew.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <h4
        className="mt-5 text-2xl text-[#0b3a5f] font-bold"
        style={{ fontFamily: "'Pacifico', cursive" }}
      >
        {crew.name}
      </h4>
      <p className="mt-3 text-slate-700 leading-relaxed text-sm md:text-base px-2">
        {display}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-sm font-semibold text-orange-600 hover:text-orange-700"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

export default AboutUs;
